import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  CATALOG_FILTER_EXISTS,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';

const ALLOWED_OKTA_GROUPS_ANNOTATION = 'schibsted.media/allowed-okta-groups';

// mirrors the backend processor's tag sanitizing (catalog tags: [a-z0-9+#] separated by '-', max 63 chars)
function toTagFormat(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9+#]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/g, '');
}

// Templates the backend policy lets through are either unrestricted or the
// user belongs to the group, so this is purely an informational highlight.
function useRestrictedTemplateGroupTags(): string[] {
  const catalogApi = useApi(catalogApiRef);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: {
          kind: 'template',
          [`metadata.annotations.${ALLOWED_OKTA_GROUPS_ANNOTATION}`]:
            CATALOG_FILTER_EXISTS,
        },
        fields: ['metadata.annotations'],
      })
      .then(({ items }) => {
        if (cancelled) return;
        const groups = new Set<string>();
        for (const entity of items) {
          entity.metadata.annotations?.[ALLOWED_OKTA_GROUPS_ANNOTATION]?.split(
            ',',
          )
            .map(group => toTagFormat(group.trim()))
            .filter(Boolean)
            .forEach(group => groups.add(group));
        }
        setTags([...groups]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [catalogApi]);

  return tags;
}

// Tag chips (TemplateCard, EntityAboutCard, ...) are plain MUI Chips with no
// per-tag attribute to target via CSS, so restricted ones are highlighted by
// matching their rendered label text instead.
function useHighlightRestrictedTagChips(tags: string[]) {
  useEffect(() => {
    if (tags.length === 0) return undefined;
    const restricted = new Set(tags);

    const highlight = (label: Element) => {
      const text = label.textContent?.trim().toLowerCase();
      if (!text || !restricted.has(text)) return;
      const chip = label.closest<HTMLElement>('.MuiChip-root');
      if (!chip || chip.dataset.restrictedTag) return;
      chip.dataset.restrictedTag = 'true';
      chip.style.backgroundColor = '#fef3c7';
      chip.style.color = '#92400e';
      chip.style.borderColor = '#fde68a';
    };

    const scan = (root: ParentNode) =>
      root.querySelectorAll('.MuiChip-label').forEach(highlight);

    scan(document);

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(node => {
          if (!(node instanceof Element)) return;
          if (node.matches('.MuiChip-label')) highlight(node);
          scan(node);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [tags]);
}

function RestrictedTemplateTagsHighlighter() {
  const tags = useRestrictedTemplateGroupTags();
  useHighlightRestrictedTagChips(tags);
  return null;
}

export const restrictedTemplateTagsWrapper = AppRootWrapperBlueprint.make({
  params: {
    component: ({ children }) => (
      <>
        {children}
        <RestrictedTemplateTagsHighlighter />
      </>
    ),
  },
});
